import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, Check, Loader2 } from "lucide-react";

/**
 * Admin panel — silently-dropped invoice lines the wholesale gate excluded
 * from commission calculation. Populated by ``service.log_wholesale_gate_drops``
 * on every webhook / backfill pass.
 *
 * A "drop" is a Syncro invoice line whose ``product_id`` isn't in the
 * local wholesale product master — typically because Syncro has a
 * duplicate SKU under a different pid (rename/re-create leaves the old
 * product orphaned). Admin actions:
 *   1. Open the invoice in Syncro to inspect the line item.
 *   2. Either add the product_id to the wholesale master (Syncro admin →
 *      set the product category to "Wholesale"), OR
 *   3. Click "Mark resolved" once the underlying data is fixed so the
 *      row drops off the unresolved count.
 */
export default function WholesaleGateDropsPanel() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState({ unresolved_count: 0, unresolved_total_ex_gst: 0 });
  const [busyId, setBusyId] = useState(null);
  const [includeResolved, setIncludeResolved] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/commissions/wholesale-gate-drops", {
        params: { include_resolved: includeResolved ? 1 : 0 },
      });
      setRows(data.drops || []);
      setSummary(data.summary || { unresolved_count: 0, unresolved_total_ex_gst: 0 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load gate drops");
      setRows([]);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [includeResolved]);

  async function resolve(row) {
    const drop_id = `${row.invoice_id}:${row.product_id}:${row.line_index}`;
    setBusyId(drop_id);
    try {
      await api.post(`/commissions/wholesale-gate-drops/${encodeURIComponent(drop_id)}/resolve`, {});
      toast.success("Marked resolved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to resolve");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section data-testid="gate-drops-panel" className="rounded-xl border border-slate-800 bg-slate-900 p-5 md:p-6 space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Wholesale gate drops
          </div>
          <h2 className="font-display text-xl mt-1">Silently-dropped invoice lines</h2>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Syncro invoice lines whose product_id isn&rsquo;t in your wholesale master, so they were excluded from commission calc.
            Usually a duplicate SKU (product renamed or re-created in Syncro under a new id).
            Fix the master or mark each row resolved to clear the count.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-2xl font-mono font-semibold text-amber-300" data-testid="gate-drops-count">
              {summary.unresolved_count}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">unresolved</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-mono text-slate-200" data-testid="gate-drops-total">
              ${summary.unresolved_total_ex_gst.toFixed(2)}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">missed ex-GST</div>
          </div>
        </div>
      </header>

      <div className="flex items-center gap-4 text-xs">
        <label className="inline-flex items-center gap-2 text-slate-400">
          <input
            type="checkbox"
            data-testid="gate-drops-include-resolved"
            checked={includeResolved}
            onChange={(e) => setIncludeResolved(e.target.checked)}
            className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-cyan-500/30"
          />
          Include resolved
        </label>
        <button
          type="button"
          onClick={load}
          data-testid="gate-drops-refresh"
          className="text-cyan-400 hover:text-cyan-200 underline underline-offset-2"
        >
          Refresh
        </button>
      </div>

      {rows === null ? (
        <div className="text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="gate-drops-empty" className="rounded-lg border border-dashed border-slate-800 bg-slate-950/40 p-8 text-center text-sm text-slate-500">
          Nothing here — every Syncro invoice line has matched the wholesale master.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-2">Invoice</th>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-left px-4 py-2">Product</th>
                <th className="text-left px-4 py-2">pid</th>
                <th className="text-right px-4 py-2">Ex-GST</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800" data-testid="gate-drops-tbody">
              {rows.map((r) => {
                const dropId = `${r.invoice_id}:${r.product_id}:${r.line_index}`;
                return (
                  <tr key={dropId} data-testid={`gate-drop-${dropId}`} className={r.resolved ? "opacity-40" : ""}>
                    <td className="px-4 py-2 font-mono text-xs">
                      {r.invoice_url ? (
                        <a href={r.invoice_url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-200">
                          #{r.invoice_id} <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-slate-300">#{r.invoice_id}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-200">{r.customer_name || "—"}</td>
                    <td className="px-4 py-2 text-slate-300 max-w-md">
                      <div className="truncate" title={r.product_name}>{r.product_name || "—"}</div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.product_id || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono text-slate-300">${(r.line_ex_gst || 0).toFixed(2)}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{r.source}</td>
                    <td className="px-4 py-2 text-right">
                      {!r.resolved && (
                        <button
                          type="button"
                          onClick={() => resolve(r)}
                          disabled={busyId === dropId}
                          data-testid={`gate-drop-resolve-${dropId}`}
                          className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-100 border border-emerald-500/40 hover:border-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full px-3 py-1 font-semibold transition-colors disabled:opacity-50"
                        >
                          {busyId === dropId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Resolve
                        </button>
                      )}
                      {r.resolved && (
                        <span className="text-xs text-emerald-400 inline-flex items-center gap-1">
                          <Check className="w-3 h-3" /> Resolved
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
